import {
	cancelSubscription,
	createCheckoutSession,
	createCustomer,
	createCustomerPortalSession,
	createPrice,
	createProduct,
	deleteCustomer,
	deletePrice,
	deleteProduct,
	getCustomer,
	getSubscription,
	getUserSubscription,
	listCustomers,
	listPrices,
	listProducts,
	listStripeLogs,
	listSubscriptions,
	reactivateSubscription,
	updateCustomer,
	updatePrice,
	updateProduct,
	verifySubscription,
} from "@bodhistreams/api/src/stripeApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Query keys factory for Stripe queries
 */
export const stripeQueryKeys = {
	all: ["stripe"],
	subscriptions: () => [...stripeQueryKeys.all, "subscriptions"],
	subscription: (id) => [...stripeQueryKeys.subscriptions(), id],
	userSubscription: () => [...stripeQueryKeys.all, "userSubscription"],
	products: () => [...stripeQueryKeys.all, "products"],
	product: (id) => [...stripeQueryKeys.products(), id],
	prices: () => [...stripeQueryKeys.all, "prices"],
	price: (id) => [...stripeQueryKeys.prices(), id],
	customers: () => [...stripeQueryKeys.all, "customers"],
	customer: (id) => [...stripeQueryKeys.customers(), id],
	logs: (params = {}) => [...stripeQueryKeys.all, "logs", params],
};

// ========== Subscription Queries ==========

/**
 * Hook to fetch user's current subscription
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useUserSubscription = (enabled = true) => {
	return useQuery({
		queryKey: stripeQueryKeys.userSubscription(),
		queryFn: async () => {
			return await getUserSubscription();
		},
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 30 * 60 * 1000, // 30 minutes
		retry: (failureCount, error) => {
			if (error?.response?.status === 401 || error?.response?.status === 403) {
				return false;
			}

			return failureCount < 3;
		},
	});
};

/**
 * Hook to fetch a specific subscription (admin only)
 * @param {string} subscriptionId - Subscription ID
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useSubscription = (subscriptionId, enabled = true) => {
	return useQuery({
		queryKey: stripeQueryKeys.subscription(subscriptionId),
		queryFn: async () => {
			return await getSubscription(subscriptionId);
		},
		enabled: enabled && !!subscriptionId,
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	});
};

/**
 * Hook to fetch all subscriptions (admin only)
 * @param {Object} params - Query parameters
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useSubscriptions = (params = {}, enabled = true) => {
	return useQuery({
		queryKey: [...stripeQueryKeys.subscriptions(), params],
		queryFn: async () => {
			return await listSubscriptions(params);
		},
		enabled,
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	});
};

/**
 * Hook to fetch stored Stripe logs (admin only)
 * @param {Object} params - Query parameters
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useStripeLogs = (params = {}, enabled = true) => {
	return useQuery({
		queryKey: stripeQueryKeys.logs(params),
		queryFn: async () => {
			return await listStripeLogs(params);
		},
		enabled,
		staleTime: 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});
};

/**
 * Hook to create checkout session
 * @returns {Object} React Query mutation result
 */
export const useCreateCheckoutSession = () => {
	return useMutation({
		mutationFn: ({ membershipId, isYearly }) =>
			createCheckoutSession(membershipId, isYearly),
	});
};

/**
 * Hook to verify subscription after payment
 * @returns {Object} React Query mutation result
 */
export const useVerifySubscription = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: verifySubscription,
		onSuccess: () => {
			// Invalidate user subscription query
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.userSubscription(),
			});
		},
	});
};

/**
 * Hook to create customer portal session
 * @returns {Object} React Query mutation result
 */
export const useCreateCustomerPortalSession = () => {
	return useMutation({
		mutationFn: createCustomerPortalSession,
	});
};

/**
 * Hook to cancel subscription
 * @returns {Object} React Query mutation result
 */
export const useCancelSubscription = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ subscriptionId, atPeriodEnd }) =>
			cancelSubscription(subscriptionId, atPeriodEnd),
		onSuccess: (data, { subscriptionId }) => {
			queryClient.setQueryData(
				stripeQueryKeys.subscription(subscriptionId),
				data,
			);
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.subscriptions(),
			});
		},
	});
};

/**
 * Hook to reactivate subscription
 * @returns {Object} React Query mutation result
 */
export const useReactivateSubscription = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: reactivateSubscription,
		onSuccess: (data, subscriptionId) => {
			queryClient.setQueryData(
				stripeQueryKeys.subscription(subscriptionId),
				data,
			);
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.subscriptions(),
			});
		},
	});
};

// ========== Products Queries ==========

/**
 * Hook to fetch all products (admin only)
 * @param {Object} params - Query parameters
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useProducts = (params = {}, enabled = true) => {
	return useQuery({
		queryKey: [...stripeQueryKeys.products(), params],
		queryFn: async () => {
			return await listProducts(params);
		},
		enabled,
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	});
};

/**
 * Hook to create a product (admin only)
 * @returns {Object} React Query mutation result
 */
export const useCreateProduct = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createProduct,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.products(),
			});
		},
	});
};

/**
 * Hook to update a product (admin only)
 * @returns {Object} React Query mutation result
 */
export const useUpdateProduct = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ productId, productData }) =>
			updateProduct(productId, productData),
		onSuccess: (updatedProduct, { productId }) => {
			queryClient.setQueryData(
				stripeQueryKeys.product(productId),
				updatedProduct,
			);
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.products(),
			});
		},
	});
};

/**
 * Hook to delete a product (admin only)
 * @returns {Object} React Query mutation result
 */
export const useDeleteProduct = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteProduct,
		onSuccess: (_, productId) => {
			queryClient.removeQueries({
				queryKey: stripeQueryKeys.product(productId),
			});
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.products(),
			});
		},
	});
};

// ========== Prices Queries ==========

/**
 * Hook to fetch all prices (admin only)
 * @param {Object} params - Query parameters
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const usePrices = (params = {}, enabled = true) => {
	return useQuery({
		queryKey: [...stripeQueryKeys.prices(), params],
		queryFn: async () => {
			return await listPrices(params);
		},
		enabled,
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	});
};

/**
 * Hook to create a price (admin only)
 * @returns {Object} React Query mutation result
 */
export const useCreatePrice = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createPrice,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.prices(),
			});
		},
	});
};

/**
 * Hook to update a price (admin only)
 * @returns {Object} React Query mutation result
 */
export const useUpdatePrice = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ priceId, priceData }) => updatePrice(priceId, priceData),
		onSuccess: (updatedPrice, { priceId }) => {
			queryClient.setQueryData(stripeQueryKeys.price(priceId), updatedPrice);
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.prices(),
			});
		},
	});
};

/**
 * Hook to delete a price (admin only)
 * @returns {Object} React Query mutation result
 */
export const useDeletePrice = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deletePrice,
		onSuccess: (_, priceId) => {
			queryClient.removeQueries({
				queryKey: stripeQueryKeys.price(priceId),
			});
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.prices(),
			});
		},
	});
};

// ========== Customers Queries ==========

/**
 * Hook to fetch all customers (admin only)
 * @param {Object} params - Query parameters
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useCustomers = (params = {}, enabled = true) => {
	return useQuery({
		queryKey: [...stripeQueryKeys.customers(), params],
		queryFn: async () => {
			return await listCustomers(params);
		},
		enabled,
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	});
};

/**
 * Hook to fetch a specific customer (admin only)
 * @param {string} customerId - Customer ID
 * @param {boolean} enabled - Whether to enable the query
 * @returns {Object} React Query result
 */
export const useCustomer = (customerId, enabled = true) => {
	return useQuery({
		queryKey: stripeQueryKeys.customer(customerId),
		queryFn: async () => {
			return await getCustomer(customerId);
		},
		enabled: enabled && !!customerId,
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
	});
};

/**
 * Hook to create a customer (admin only)
 * @returns {Object} React Query mutation result
 */
export const useCreateCustomer = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createCustomer,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.customers(),
			});
		},
	});
};

/**
 * Hook to update a customer (admin only)
 * @returns {Object} React Query mutation result
 */
export const useUpdateCustomer = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ customerId, customerData }) =>
			updateCustomer(customerId, customerData),
		onSuccess: (updatedCustomer, { customerId }) => {
			queryClient.setQueryData(
				stripeQueryKeys.customer(customerId),
				updatedCustomer,
			);
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.customers(),
			});
		},
	});
};

/**
 * Hook to delete a customer (admin only)
 * @returns {Object} React Query mutation result
 */
export const useDeleteCustomer = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteCustomer,
		onSuccess: (_, customerId) => {
			queryClient.removeQueries({
				queryKey: stripeQueryKeys.customer(customerId),
			});
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.customers(),
			});
		},
	});
};

/**
 * Hook to get the query client for manual cache operations
 * @returns {Object} React Query client
 */
export const useStripeQueryClient = () => {
	return useQueryClient();
};

/**
 * Hook to invalidate Stripe queries
 * @returns {Object} Invalidation functions
 */
export const useInvalidateStripe = () => {
	const queryClient = useQueryClient();

	return {
		invalidateAll: () =>
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.all,
			}),
		invalidateProducts: () =>
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.products(),
			}),
		invalidatePrices: () =>
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.prices(),
			}),
		invalidateSubscriptions: () =>
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.subscriptions(),
			}),
		invalidateCustomers: () =>
			queryClient.invalidateQueries({
				queryKey: stripeQueryKeys.customers(),
			}),
		invalidateLogs: () =>
			queryClient.invalidateQueries({
				queryKey: [...stripeQueryKeys.all, "logs"],
			}),
	};
};
